import { SensorType } from "../../shared.js";
import {
    decodeSproutTempHum,
    SproutSensorIDsByBitDepthByHardwareType,
    sproutSensorIDTable,
} from "../SproutSensorIDConfiguration.js";
import { combineValidators, sensorParameterNumericValidators } from "./SensorConfigurationParameterHelpers.js";
import {
    generateFlowmeterConfiguration,
    generatePressureTransducerConfiguration,
} from "./SensorConfigurationTemplates.js";
import {
    SensorConfiguration,
    SensorConfigurationBase,
    SensorParameterValueType,
    VoltageRequirement,
} from "./SensorConfigurationTypes.js";

export * from "../SproutSensorIDConfiguration.js";
export * from "./SensorConfigurationTypes.js";

/**
 * Registry of all sensor configurations in the system.
 *
 * This is the central place to define all sensor configurations. To add a new sensor:
 *
 * 1. Add a new entry to this object with a unique key
 * 2. Define all required properties according to SensorConfigurationBase
 * 3. For flowmeters, consider using generateFlowmeterConfiguration() helper
 * 4. For pressure transducers, consider using generatePressureTransducerConfiguration() helper
 *
 * The system will automatically:
 * - Convert these configurations to SensorConfiguration objects by adding the key
 * - Make them available through the SensorConfigurations export
 * - Validate them at runtime
 *
 * IMPORTANT: After adding a sensor here, make sure both frontend and backend
 * are using the latest versions of this file.
 *
 * @see SensorConfigurationBase for details on each required field
 * @see SensorConfigurationValidator for validation rules
 */
export const SensorConfigurationsNoExplicitKeys = {
    irrometer: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "2:4.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v) => {
                v = Math.max(0, v);
                return (v + 11.75) / 23.5;
            },
            rawDataToParsedDataConversion: (volts, options) => [
                {
                    dataType: options.dataType,
                    value: Math.max(0, 23.5 * volts - 11.75), // Voltages below 0.5v is considered as 0 kPa
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_irrometer",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTension",
        isOnboard: false,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Irrometer",
        modelName: `Tensiometer`,
        friendlyName: `Tensiometer`,
        sensorParameterConfigs: {},
        friendlyShortName: `Tensiometer`,

        saplingConversion: (x) => (23.5 * (254 - x)) / 50.8 - 11.75,
    },
    voltage5v: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3:4.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v) => v,
            rawDataToParsedDataConversion: (volts, options) => [
                {
                    dataType: options.dataType,
                    value: volts, // Voltages below 0.5v is considered as 0 kPa
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "voltage",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "electricalVoltage",
        isOnboard: false,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Verdi",
        modelName: `5V Sensor`,
        friendlyName: `5V Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `5V Sensor`,
    },
    teros_vol: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "2:4.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, options) => estimateRawValueFromParsedValue("teros_vol", v, options),
            rawDataToParsedDataConversion: (volts, options) => {
                const mv = volts * 1000; // convert volts to mv.
                const transformedValue =
                    5.439e-10 * Math.pow(mv, 3) - 2.731e-6 * Math.pow(mv, 2) + 4.868e-3 * mv - 2.683;
                return [
                    {
                        dataType: options.dataType,
                        value: 100 * transformedValue,
                    },
                ];
            },
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_vol",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Meter Group",
        modelName: `Teros 10`,
        friendlyName: `Teros 10`,
        sensorParameterConfigs: {},
        friendlyShortName: `Teros 10`,

        saplingConversion: (x) => {
            const mv = x * 10; // convert mV to V
            const transformedValue = 5.439e-10 * Math.pow(mv, 3) - 2.731e-6 * Math.pow(mv, 2) + 4.868e-3 * mv - 2.683;
            // 0-1 to 0-100
            return 100 * transformedValue;
        },
    },
    v1flowmeter: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 2,
            offset: 0.509375,
            kFactor: 0.128,
        }),
        manufacturerName: "Verdi",
        modelName: `F1 3/4"`,
        friendlyName: `3/4" Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `3/4" Flow`,
    } as const,
    verdiFlowmeterQuarterInch: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 20,
            offset: 27.2727273,
            kFactor: 0.00726,
        }),
        manufacturerName: "Verdi",
        modelName: `F1 1/4"`,
        friendlyName: `1/4" Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `1/4" Flow`,
    } as const,

    // Badger meter 228PV15xx
    badgerMeterFlowmeterOneHalfInch: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 3.26,
            offset: -0.316,
            kFactor: 6.4238238,
        }),
        manufacturerName: "Badger Meter",
        modelName: `228PV15`,
        friendlyName: `1 1/2" Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `1 1/2" Flow`,
    } as const,

    // Badger meter 228PV20xx
    badgerMeterFlowmeterTwoInch: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 3.374,
            offset: 0.1435,
            kFactor: 10.7615,
            precisionImportance: "high",
        }),
        manufacturerName: "Badger Meter",
        modelName: `228PV20`,
        friendlyName: `2" Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `2" Flow`,
    } as const,

    // Badger meter 228PV30xx
    badgerMeterFlowmeterThreeInch: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 2.18,
            offset: 0.227,
            kFactor: 31.4528886,
        }),
        manufacturerName: "Badger Meter",
        modelName: `228PV30`,
        friendlyName: `3" Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `3" Flow`,
    } as const,

    // Badger meter 228PV40xx
    badgerMeterFlowmeterFourInch: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 2.674,
            offset: 0.23707,
            kFactor: 52.022108682,
        }),
        manufacturerName: "Badger Meter",
        modelName: `228PV40`,
        friendlyName: `4" Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `4" Flow`,
    } as const,

    // Flomec QS200-10 Sch40
    flomecFlowmeterOneInchSch40: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.3932965084,
            maxRawValueHz: 59.19349378,
            offset: 0,
            litersPerPulse: 0.0352,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-10`,
        friendlyName: `QS200 1" Sch 40 Flowmeter`,
        friendlyShortName: `1" Flow`,
    } as const,

    // Flomec QS200-10 Sch80
    flomecFlowmeterOneInchSch80: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.3932965084,
            maxRawValueHz: 59.19349378,
            offset: 0,
            litersPerPulse: 0.0338,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-10`,
        friendlyName: `QS200 1" Sch 80 Flowmeter`,
        friendlyShortName: `1" Flow`,
    } as const,

    // Flomec QS200-15 Sch40
    flomecFlowmeterOneAndAHalfInchSch40: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.6935224901,
            maxRawValueHz: 103.4982289,
            offset: 0,
            litersPerPulse: 0.05,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-15`,
        friendlyName: `QS200 1 1/2" Sch 40 Flowmeter`,
        friendlyShortName: `1 1/2" Flow`,
    },

    // Flomec QS200-15 Sch80
    flomecFlowmeterOneAndAHalfInchSch80: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.6935224901,
            maxRawValueHz: 103.4982289,
            offset: 0,
            litersPerPulse: 0.0496,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-15`,
        friendlyName: `QS200 1 1/2" Sch 80 Flowmeter`,
        friendlyShortName: `1 1/2" Flow`,
    } as const,

    // Flomec QS200-20 Sch40
    flomecFlowmeterTwoInchSch40: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.6292393855,
            maxRawValueHz: 94.45642603,
            offset: 0,
            litersPerPulse: 0.0922,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-20`,
        friendlyName: `QS200 2" Sch 40 Flowmeter`,
        friendlyShortName: `2" Flow`,
    } as const,

    // Flomec QS200-20 Sch80
    flomecFlowmeterTwoInchSch80: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.6292393855,
            maxRawValueHz: 94.45642603,
            offset: 0,
            litersPerPulse: 0.0919,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-20`,
        friendlyName: `QS200 2" Sch 80 Flowmeter`,
        friendlyShortName: `2" Flow`,
    } as const,

    // Flomec QS200-30 Sch40
    flomecFlowmeterThreeInchSch40: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.4973430543,
            maxRawValueHz: 74.58232957,
            offset: 0,
            litersPerPulse: 0.2614,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-30`,
        friendlyName: `QS200 3" Sch 40 Flowmeter`,
        friendlyShortName: `3" Flow`,
    } as const,

    // Flomec QS200-30 Sch80
    flomecFlowmeterThreeInchSch80: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.4973430543,
            maxRawValueHz: 74.58232957,
            offset: 0,
            litersPerPulse: 0.2496,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-30`,
        friendlyName: `QS200 3" Sch 80 Flowmeter`,
        friendlyShortName: `3" Flow`,
    } as const,

    // Flomec QS200-40 Sch40
    flomecFlowmeterFourInchSch40: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.475180049,
            maxRawValueHz: 71.28682658,
            offset: 0,
            litersPerPulse: 0.4753,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-40`,
        friendlyName: `QS200 4" Sch 40 Flowmeter`,
        friendlyShortName: `4" Flow`,
    } as const,

    // Flomec QS200-40 Sch80
    flomecFlowmeterFourInchSch80: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0.475180049,
            maxRawValueHz: 71.28682658,
            offset: 0,
            litersPerPulse: 0.4627,
        }),
        manufacturerName: "Flomec",
        modelName: `QS200-40`,
        friendlyName: `QS200 4" Sch 80 Flowmeter`,
        friendlyShortName: `4" Flow`,
    } as const,

    // McCrometer FC101 Flowrate sensor
    // This sensor's equation will change for every instllation, use polynomial conversion
    mccrometerFlowmeterFC101: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0,
            offset: 0,
            kFactor: 1,
            skipInverseFormula: true,
            precisionImportance: "high",
        }),
        manufacturerName: "McCrometer",
        modelName: `FC101`,
        friendlyName: `FC101 Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `FC101 Flow`,
    } as const,

    // Any flow meter we don't use very much.
    // This sensor's equation will change for every installation, uses polynomial conversion
    genericFlowmeter: {
        ...generateFlowmeterConfiguration({
            minRawValueHz: 0,
            offset: 0,
            kFactor: 1,
            skipInverseFormula: true,
            precisionImportance: "high",
        }),
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "2:4.*.*.*.*",
            rawDataFormat: "PWM",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (freq, options) => {
                const parameters = options.sensorParameters as {
                    litersPerPulse: number;
                    offset: number;
                    minValue: number;
                };

                let value = parameters.litersPerPulse * (freq + parameters.offset) * 60;

                // ensure that very small values aren't recorded as flow-on
                if (freq <= parameters.minValue) {
                    value = 0;
                }

                return [
                    {
                        dataType: options.dataType,
                        value: value,
                    },
                ];
            },
            parsedDataToRawDataConversion: (value, options) => {
                const parameters = options.sensorParameters as {
                    litersPerPulse: number;
                    offset: number;
                    minValue: number;
                };

                const frequency = value / parameters.litersPerPulse / 60 - parameters.offset;

                // If value is too low, the output should be 0
                if (frequency <= parameters.minValue) {
                    return 0;
                }

                // Regular conversion if data is in range
                return frequency;
            },

            precisionImportance: "high",
            rawAndParsedValuesIncreaseTogether: true,
            supportsLowPowerTotalization: false,
        },
        sensorParameterConfigs: {
            litersPerPulse: {
                key: "litersPerPulse",
                type: SensorParameterValueType.NUMBER,
                name: "K-Factor",
                parameterUnitCategory: "flowmeterKFactor",
                // this is because rawAndParsedValuesIncreaseTogether is true, which necessitates a positive k value
                mustBeNonNegative: true,
                mustBeInteger: false,
                defaultValue: 1,
                validator: combineValidators(
                    sensorParameterNumericValidators.isNumber,
                    sensorParameterNumericValidators.isGreaterThanZero,
                ),
            },
            offset: {
                key: "offset",
                type: SensorParameterValueType.NUMBER,
                name: "Offset",
                parameterUnitCategory: "flowmeterOffset",
                mustBeNonNegative: false,
                mustBeInteger: false,
                defaultValue: 0,
                validator: combineValidators(sensorParameterNumericValidators.isNumber),
            },
            minValue: {
                key: "minValue",
                type: SensorParameterValueType.NUMBER,
                name: "Minimum Frequency",
                parameterUnitCategory: "flowmeterMinimumFrequency",
                description:
                    "Frequencies below this value reported by the sensor will be set to zero. Setting this is recommended as flow meters often report low flow when no water is present.",
                mustBeNonNegative: true,
                mustBeInteger: false,
                defaultValue: 0,
                validator: combineValidators(
                    sensorParameterNumericValidators.isNumber,
                    sensorParameterNumericValidators.isGreaterThanOrEqualToZero,
                ),
            },
        },
        manufacturerName: "Unknown",
        modelName: `N/A`,
        friendlyName: `Generic Flowmeter`,
        friendlyShortName: `Flowmeter`,
    },

    genericPulseTotalizer: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.block.*.*.8+",
            rawDataFormat: "PULSE_TOTAL",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (pulseTotal, options) => [
                {
                    dataType: options.dataType,
                    value: pulseTotal,
                },
            ],
            parsedDataToRawDataConversion: (pulseTotal) => pulseTotal,

            precisionImportance: "high",
            rawAndParsedValuesIncreaseTogether: true,
            supportsLowPowerTotalization: true,
        },
        sensorParameterConfigs: {},
        manufacturerName: "Unknown",
        modelName: `N/A`,
        friendlyName: `Generic Pulse Totalizer`,
        friendlyShortName: `Pulse Total`,
        parsedDataType: "gen4FlowPulseTotal",
        unitCategory: "electricalPulseCount",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,
    },

    // Sensirion SLF3S-1300F for R&D project
    sensirionFlowmeterSLF3S_1300F: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "PWM",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (freq, options) => [
                {
                    dataType: options.dataType,
                    // Divide by 1000 to convert ml/min to L/min
                    // 500 Hz is equal to 0 ml/min as this sensor can measure negative flow
                    value: 499.9 < freq && freq < 500.1 ? 0 : (0.13 * freq - 65) / 1000,
                },
            ],

            parsedDataToRawDataConversion: (value, _options) =>
                -0.000013 < value && value < 0.000013 ? 500 : (value * 1000 + 65) * 0.13,
            supportsLowPowerTotalization: false,
            precisionImportance: "high",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "v1flowmeter",
        useZeroOrderHoldInterpolation: true,
        unitCategory: "liquidFlow",
        isOnboard: false,
        communicationProtocol: "PWM",
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Sensirion",
        modelName: `SLF3S-1300F`,
        friendlyName: `SLF3S Flowmeter`,
        sensorParameterConfigs: {},
        friendlyShortName: `SLF3S Flow`,
    },

    v1transducer: {
        ...generatePressureTransducerConfiguration({
            voltageMultiplier: 37.5,
            voltageOffset: 18.75,
            pressureConversionFactor: 6.89476,
        }),
        manufacturerName: "Keller",
        modelName: `21Y`,
        friendlyName: `Pressure Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Press. Sense`,
    } as const,

    honeywell150Psig0_5VTransducer: {
        ...generatePressureTransducerConfiguration({
            voltageMultiplier: 37.5,
            voltageOffset: 18.75,
            pressureConversionFactor: 6.89476,
        }),
        manufacturerName: "Honeywell",
        modelName: `MIPAN1XX150PSAAX`,
        friendlyName: `150 PSIG Pressure Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `150 PSIG Press.`,
    } as const,

    honeywell45Psig0_5VTransducer: {
        ...generatePressureTransducerConfiguration({
            // 60 PSIA over 0.5–4.5V: slope = 60 / 4.0 = 15
            voltageMultiplier: 15,
            // Offset includes atmospheric subtraction for PSIG output:
            // (slope * V_min) + atmospheric = (15 * 0.5) + 14.696 = 22.196
            voltageOffset: 22.196,
            pressureConversionFactor: 6.89476,
            precisionImportance: "high",
            requiredVoltage: VoltageRequirement.VOLTS_5,
        }),
        manufacturerName: "Honeywell",
        modelName: `MIPAN2XX060PAAAX`,
        friendlyName: `45 PSIG Pressure Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `45 PSIG Press.`,
    } as const,

    waterLevelSensorAST400_5: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, _options) => {
                v = Math.max(0, v);
                return (v * 5) / 3.516248;
            },
            rawDataToParsedDataConversion: (volt, options) =>
                // 5 volts = 5 PSI
                // 5 PSI = 3.5162480 meters of pressure head
                //
                [
                    {
                        dataType: options.dataType,
                        value: (volt * 3.516248) / 5.0,
                    },
                ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "waterTankLevel",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "liquidTankLevel",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Paremtech",
        modelName: `AST400-5`,
        friendlyName: `AST400-5 Tank Level Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Tank Level`,
    },

    waterLevelSensorAST400_40: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, _options) => {
                v = Math.max(0, v);
                return (v / 28.12998459) * 5;
            },
            rawDataToParsedDataConversion: (volt, options) =>
                // 5 volts = 40 PSI
                // 40 PSI = 28.12998459 meters of pressure head
                [
                    {
                        dataType: options.dataType,
                        value: (volt * 28.12998459) / 5,
                    },
                ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "waterTankLevel",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "liquidTankLevel",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Paremtech",
        modelName: `AST400-40`,
        friendlyName: `AST400-40 Tank Level Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Tank Level`,
    },

    waterLevelSensorAST400_10: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, _options) => {
                v = Math.max(0, v);
                return (v / 7.0324961475) * 5;
            },
            rawDataToParsedDataConversion: (volt, options) =>
                // 5 volts = 10 PSI
                // 10 PSI = 7.0324961475 meters of pressure head
                [
                    {
                        dataType: options.dataType,
                        value: (volt * 7.0324961475) / 5,
                    },
                ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "waterTankLevel",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "liquidTankLevel",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Paremtech",
        modelName: `AST400-10`,
        friendlyName: `AST400-10 Tank Level Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Tank Level`,
    },

    tankLevelSensorTEConnectivityUS331: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, _options) => {
                v = Math.max(0, v);
                // meters to voltage: voltage = (v + 1.31825) / 2.6365
                return (v + 1.31825) / 2.6365;
            },
            rawDataToParsedDataConversion: (volt, options) =>
                // 0.5V = 0 m, 4.5V = 10.546 m
                // meters = 2.6365 * voltage - 1.31825
                [
                    {
                        dataType: options.dataType,
                        value: Math.max(0, 2.6365 * volt - 1.31825), // Voltage below 0.5V is considered as 0 m
                    },
                ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "waterTankLevel",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "liquidTankLevel",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "TE Connectivity",
        modelName: `US331-000005-015PG`,
        friendlyName: `0-15 PSI Tank Level Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Tank Level`,
    },

    kellerLiquidLevelEconoline_34ft: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_12,
            parsedDataToRawDataConversion: (v, _options) => {
                v = Math.max(0, v);
                // 5V = 34.5 ft water column (34.5'/10.5156 m WC)
                return (v / 10.5156) * 5;
            },
            rawDataToParsedDataConversion: (volt, options) => [
                {
                    dataType: options.dataType,
                    value: (volt * 10.5156) / 5,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "high",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "waterTankLevel",
        useZeroOrderHoldInterpolation: true,
        unitCategory: "liquidTankLevel",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Keller",
        modelName: `0922.31007.041307`,
        friendlyName: `Submersible Tank Level Sensor, 34.5ft WC`,
        sensorParameterConfigs: {},
        friendlyShortName: `Tank Level`,
    },

    watermark: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, _options) => v / 85.357,
            rawDataToParsedDataConversion: (volt, options) => [
                {
                    dataType: options.dataType,
                    value: volt * 85.357,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_watermark",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTension",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Irrometer",
        modelName: `200SS`,
        friendlyName: `Watermark`,
        sensorParameterConfigs: {},
        friendlyShortName: `Watermark`,
    },

    draginoECV1Sensor: {
        verdiDeviceSensorConfiguration: null,

        parsedDataType: "draginoEC",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilElectricalConductivity",
        isOnboard: true,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Dragino",
        modelName: `LSE01`,
        friendlyName: `Electrical Conductivity Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `EC Sensor`,
    },

    FloraPulseMicrotensiometer: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "stemWaterPotential",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "stemWaterPotential",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "stemWaterPotential",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "FloraPulse",
        modelName: "Microtensiometer",
        friendlyName: "FloraPulse Microtensiometer",
        sensorParameterConfigs: {},
        friendlyShortName: "Microtensiometer",
    },

    //aquacheck probes
    AquaCheckSoilTemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
            supportsLowPowerTotalization: false,
        },

        sensorParameterConfigs: {},
        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "AquaCheck USA",
        modelName: `Multi-Depth SDI-12 Probe - Temperature`,
        friendlyName: `Multi-Depth SDI-12 Probe - Temperature`,
        friendlyShortName: `AquaCheck Temp.`,
    },
    AquaCheckSoilMoistureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: rawReading,
                },
            ],
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
            supportsLowPowerTotalization: false,
        },
        sensorParameterConfigs: {},
        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "AquaCheck USA",
        modelName: `Multi-Depth SDI-12 Probe - Moisture`,
        friendlyName: `Multi-Depth SDI-12 Probe - Moisture`,
        friendlyShortName: `AquaCheck Moist.`,
    },

    //Sentek Probes
    SentekDrillAndDropSoilTemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Sentek Technologies",
        modelName: `Drill And Drop - Soil Temperature Probe`,
        friendlyName: `Drill And Drop Soil Temperature Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `D&D Temp.`,
    },
    SentekDrillAndDropSoilMoistureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Sentek Technologies",
        modelName: `Drill And Drop - Soil Moisture Probe`,
        friendlyName: `Drill And Drop Soil Moisture Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `D&D Moisture`,
    },
    SentekDrillAndDropSoilSalinitySensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "salinity_drillAndDrop",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "salinity_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilVolumetricIonContent",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Sentek Technologies",
        modelName: `Drill And Drop - Soil Salinity Probe`,
        friendlyName: `Drill And Drop Soil Salinity Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `D&D Salinity`,
    },

    //  EnviroPro Probes   //
    EnviroProMoistureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "EnviroPro",
        modelName: `EnviroPro G Series Soil Probe`,
        friendlyName: `EnviroPro G Series Soil Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `EnviroPro Moisture`,
    },
    EnviroProTemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "EnviroPro",
        modelName: `EnviroPro G Series Soil Probe`,
        friendlyName: `EnviroPro G Series Soil Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `EnviroPro Temperature`,
    },
    EnviroProSalinitySensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "salinity_drillAndDrop",
                    // Raw outputs as dS/m, should be converted to µS/cm for the database
                    value: rawReading * 1000,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "salinity_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilElectricalConductivity",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "EnviroPro",
        modelName: `EnviroPro G Series Soil Probe with Salinity Sensor`,
        friendlyName: `EnviroPro G Series Soil Probe with Salinity Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `EnviroPro Salinity`,
    },

    //  HSTI Hydrascout Probes   //
    HydrascoutMoistureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "HSTI",
        modelName: `Hydrascout`,
        friendlyName: `Hydrascout Soil Moisture Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `Hydrascout Moisture`,
    },
    HydrascoutTemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: false,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "HSTI",
        modelName: `Hydrascout`,
        friendlyName: `Hydrascout Soil Temperature Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `Hydrascout Temperature`,
    },
    HydrascoutSalinitySensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "salinity_drillAndDrop",
                    // TODO: When this sensor is added, ensure this gets converted to µS/cm to match the other salinity sensors
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "salinity_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilElectricalConductivity",
        isOnboard: false,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "HSTI",
        modelName: `Hydrascout`,
        friendlyName: `Hydrascout EC Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `Hydrascout Salinity`,
    },
    //  HSTI Hydrascout Probes for SANDY SOIL  //
    HydrascoutMoistureSensorSandy: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v, _options) => (v + 10.836) / 85.357,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: 0.4853 * rawReading - 10.836, //values from HSTI calibration table
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "HSTI",
        modelName: `Hydrascout Sandy`,
        friendlyName: `Hydrascout Soil Moisture Probe Sandy`,
        sensorParameterConfigs: {},
        friendlyShortName: `Hydrascout Moisture Sandy`,
    },

    //  Teros 12   //
    Teros12MoistureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            // Conversion for Mineral Soils, taken from Teros 12 manual-18226-10 6.2025 //
            parsedDataToRawDataConversion: (v) => (v / 100 + 0.6956) / 3.879e-4,
            rawDataToParsedDataConversion: (rawReading) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: (3.879e-4 * rawReading - 0.6956) * 100,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Meter Group",
        modelName: `Teros 12`,
        friendlyName: `Teros 12`,
        sensorParameterConfigs: {},
        friendlyShortName: `Teros 12 Moisture`,
    },
    Teros12TemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Meter Group",
        modelName: `Teros 12`,
        friendlyName: `Teros 12`,
        sensorParameterConfigs: {},
        friendlyShortName: `Teros 12 Temperature`,
    },
    Teros12SalinitySensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) => [
                {
                    dataType: "salinity_drillAndDrop",
                    // Raw outputs as µS/cm
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "salinity_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilElectricalConductivity",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Meter Group",
        modelName: `Teros 12`,
        friendlyName: `Teros 12`,
        sensorParameterConfigs: {},
        friendlyShortName: `Teros 12 Salinity`,
    },

    // Delta-T WET150 probe
    Wet150SalinitySensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) =>
                rawReading < -8000
                    ? []
                    : [
                          // sensor outputs -8020 for invalid reading, this will catch and prevent errors from being graphed
                          {
                              dataType: "salinity_drillAndDrop" as const,
                              // convert mS/m to uS/cm
                              value: rawReading * 10,
                          },
                      ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "salinity_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilElectricalConductivity",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Delta-T",
        modelName: `WET150`,
        friendlyName: `WET150`,
        sensorParameterConfigs: {},
        friendlyShortName: `WET150 Bulk Soil EC`,
    },
    // Soil-calibrated measurement modes (aM1!-aM5!) report pore EC instead of bulk EC
    Wet150PoreSalinitySensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) =>
                rawReading < -8000
                    ? []
                    : [
                          // sensor outputs -8020 for invalid reading, this will catch and prevent errors from being graphed
                          {
                              dataType: "salinity_drillAndDrop" as const,
                              // convert mS/m to uS/cm
                              value: rawReading * 10,
                          },
                      ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "salinity_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "poreSoilElectricalConductivity",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Delta-T",
        modelName: `WET150`,
        friendlyName: `WET150`,
        sensorParameterConfigs: {},
        friendlyShortName: `WET150 Pore Soil EC`,
    },
    Wet150TemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "Delta-T",
        modelName: `WET150`,
        friendlyName: `WET150`,
        sensorParameterConfigs: {},
        friendlyShortName: `WET150 Temperature`,
    },

    //  GroPoint GPLP Profile Probes   //
    GroPointMoistureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "moisture_drillAndDrop",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "moisture_drillAndDrop",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "volumetricSoilMoisture",
        isOnboard: false,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "GroPoint",
        modelName: `Profile`,
        friendlyName: `GroPoint Moisture Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `GroPoint Moisture`,
    },
    GroPointTemperatureSensor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.mega.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            rawDataToParsedDataConversion: (rawReading, _options) => [
                {
                    dataType: "soilTemperature",
                    value: rawReading,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "soilTemperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "soilTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: false,

        manufacturerName: "GroPoint",
        modelName: `Profile`,
        friendlyName: `GroPoint Soil Temperature Probe`,
        sensorParameterConfigs: {},
        friendlyShortName: `GroPoint Temperature`,
    },
    v1temphum: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "TEMPHUM",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            // The first argument is deliberately unused: a TEMPHUM reading packs two half-width
            // fields into one encoded value, so it cannot be decoded by the generic
            // bit-depth-normalize-then-range-map path that produced `volt`. The split is shared with
            // the data-stream path via decodeSproutTempHum so the two cannot drift.
            rawDataToParsedDataConversion: (_volt, options) => {
                const values = [];
                const { temperatureCelsius, humidityPercentRh } = decodeSproutTempHum({
                    encodedValue: options.rawValue,
                    bitDepth: sproutSensorIDTable[options.sensorID].bitDepth,
                });

                if (temperatureCelsius !== null) {
                    values.push({ dataType: "temperature", value: temperatureCelsius });
                }
                if (humidityPercentRh !== null) {
                    values.push({ dataType: "humidity", value: humidityPercentRh });
                }
                return values;
            },
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "temperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "boardTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Verdi",
        modelName: `2HOT`,
        friendlyName: `Temperature Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Temperature`,
    },

    v1onboardTemperature: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v) => (v + 45) / 175,
            rawDataToParsedDataConversion: (normalizedTempReading, _options) => [
                {
                    dataType: "temperature",
                    value: 175 * normalizedTempReading - 45,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "temperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "boardTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Verdi",
        modelName: `Verdi 2HOT`,
        friendlyName: `Temperature Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Temperature`,
    },

    v1onboardHumidity: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "3+.*.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v) => (v + 6) / 125,
            rawDataToParsedDataConversion: (normalizedHumidityReading, _options) => [
                {
                    dataType: "humidity",
                    value: normalizedHumidityReading * 125 - 6,
                },
            ],
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "humidity",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "humidity",
        isOnboard: true,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Verdi",
        modelName: `Verdi 2HOT`,
        friendlyName: `Humidity Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Humidity`,
    },

    gen4OnboardTemperature: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.block.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,
            parsedDataToRawDataConversion: (v) =>
                // Input is in Celsius (-128 to 127)
                // Convert to unsigned byte (0-255)
                v < 0 ? v + 256 : v,
            rawDataToParsedDataConversion: (rawValue) => {
                // Convert unsigned byte (0-255) to signed (-128 to 127)
                const signedValue = rawValue > 127 ? rawValue - 256 : rawValue;

                return [
                    {
                        dataType: "temperature",
                        value: signedValue,
                    },
                ];
            },
            supportsLowPowerTotalization: false,
            precisionImportance: "medium",
            rawAndParsedValuesIncreaseTogether: true,
        },

        parsedDataType: "temperature",
        useZeroOrderHoldInterpolation: false,
        unitCategory: "boardTemperature",
        isOnboard: true,
        openValveIncreasesDatabaseValue: true,

        manufacturerName: "Verdi",
        modelName: `Verdi 2HOT`,
        friendlyName: `Temperature Sensor`,
        sensorParameterConfigs: {},
        friendlyShortName: `Temperature`,
    },
    amphenolJS8746NtcThermistor: {
        verdiDeviceSensorConfiguration: {
            supportedDeviceVersions: "4.block.*.*.*",
            rawDataFormat: "ADC",
            requiredVoltage: VoltageRequirement.VOLTS_5,

            rawDataToParsedDataConversion: (volt, options) => {
                // Amphenol Thermometrics JS8746 10K NTC thermistor
                // Circuit: 5V → R_pullup (10kΩ) → ADC_pin → R_ntc → GND
                // V_adc = 5 * R_ntc / (R_pullup + R_ntc)
                const R_pullup = 10000; // 10kΩ pullup resistor

                // Steinhart-Hart coefficients derived from datasheet R-T table
                // Calibration points: 0°C/32639Ω, 25°C/10000Ω, 85°C/1070Ω
                // Max error: 0.079°C across -40°C to 125°C range
                const A = 1.1310517387e-3;
                const B_coeff = 2.3379815973e-4;
                const C = 8.9085613134e-8;

                // Guard against rail voltages (division by zero)
                if (volt <= 0.01 || volt >= 4.99) {
                    return [];
                }

                // Voltage divider: R_ntc = R_pullup * V / (V_supply - V)
                const R_ntc = (R_pullup * volt) / (5.0 - volt);
                const lnR = Math.log(R_ntc);
                const T_kelvin = 1.0 / (A + B_coeff * lnR + C * lnR * lnR * lnR);
                const T_celsius = T_kelvin - 273.15;

                // Do NOT clamp — thermistors can read negative temperatures
                return [{ dataType: options.dataType, value: T_celsius }];
            },

            parsedDataToRawDataConversion: (tempC, _options) => {
                // Exact inverse using Steinhart-Hart cubic solution (Cardano's formula)
                // Matches the forward conversion coefficients exactly
                const R_pullup = 10000;
                const A = 1.1310517387e-3;
                const B_coeff = 2.3379815973e-4;
                const C = 8.9085613134e-8;

                const T_kelvin = tempC + 273.15;
                // Solve: C*x³ + B_coeff*x + (A - 1/T) = 0, where x = ln(R)
                const p = B_coeff / C;
                const q = (A - 1.0 / T_kelvin) / C;
                const D = (q * q) / 4 + (p * p * p) / 27;
                const sqrtD = Math.sqrt(D);
                const x = Math.cbrt(-q / 2 + sqrtD) + Math.cbrt(-q / 2 - sqrtD);
                const R_ntc = Math.exp(x);
                const volt = (5.0 * R_ntc) / (R_pullup + R_ntc);
                return volt;
            },

            supportsLowPowerTotalization: false,
            precisionImportance: "high",
            // As temperature increases, NTC resistance decreases, voltage decreases
            rawAndParsedValuesIncreaseTogether: false,
        },

        sensorParameterConfigs: {},
        parsedDataType: "temperature",
        unitCategory: "airTemperature",
        isOnboard: false,
        communicationProtocol: "ADC",
        openValveIncreasesDatabaseValue: false,
        useZeroOrderHoldInterpolation: false,

        manufacturerName: "Amphenol",
        modelName: "JS8746B-0.20",
        friendlyName: "10K NTC Thermistor",
        friendlyShortName: "NTC Temp",
    } as const,
} as const satisfies Record<string, SensorConfigurationBase>;

/**
 * The complete registry of all sensor configurations with keys added.
 *
 * This object is automatically generated from SensorConfigurationsNoExplicitKeys
 * by adding the key to each configuration. This ensures that the key is always
 * consistent with the object property name.
 *
 * Use this object when looking up sensor configurations by type.
 */
export const SensorConfigurations: Record<SensorType, SensorConfiguration> = Object.fromEntries(
    Object.keys(SensorConfigurationsNoExplicitKeys).map(
        (sensorType: SensorType): any =>
            [sensorType, { ...SensorConfigurationsNoExplicitKeys[sensorType], key: sensorType }] as [
                SensorType,
                SensorConfiguration,
            ],
    ),
) as Record<SensorType, SensorConfiguration>;

/**
 * Performs a binary search to determine a raw value that would produce a given parsed value.
 *
 * This function is used when we need to convert from a parsed value back to a raw value,
 * but don't have an explicit inverse formula. It works by:
 * 1. Determining the valid range of raw values
 * 2. Determining if the conversion function is increasing or decreasing
 * 3. Performing a binary search to find the raw value that produces the target parsed value
 *
 * @param sensorType The type of sensor to use for conversion
 * @param parsedValue The target parsed value to match
 * @param options Additional conversion options
 * @returns The raw value that would produce the given parsed value (approximated)
 */
const estimateRawValueFromParsedValue = (sensorType, parsedValue, options) => {
    // Get the sensor configuration and determine valid raw value range
    const sensorConfig = SensorConfigurations[sensorType];

    // Get a representative sensor ID for this sensor type to determine min/max values
    const sensorID = Object.values(
        SproutSensorIDsByBitDepthByHardwareType[sensorConfig.verdiDeviceSensorConfiguration.rawDataFormat],
    )[1] as number;

    // Get the min and max raw values from the sensor ID table, with fallbacks
    const minRawValue = sproutSensorIDTable[sensorID].minValue ?? 0;
    const maxRawValue = sproutSensorIDTable[sensorID].maxValue ?? 1;

    // Create a test function that converts a raw value to a parsed value
    const test = (x) => sensorConfig.verdiDeviceSensorConfiguration.rawDataToParsedDataConversion(x, options)[0].value;

    // Determine if the conversion function is increasing or decreasing
    // This is needed because binary search needs to know which direction to adjust
    // negative 1 if the overall slope is negative and positive 1 if the overall slope is positive
    const overallTrend = test(maxRawValue) > test(minRawValue) ? 1 : -1;

    // Perform binary search to find the raw value
    let max = maxRawValue;
    let min = minRawValue;

    // Use 34 iterations for high precision (can resolve to ~2^-34 of the range)
    for (let i = 0; i < 34; i++) {
        // Calculate midpoint
        const midPoint = (max + min) / 2;

        // Convert midpoint to parsed value
        const convertedMidpoint = test(midPoint);

        // Log current search state for debugging
        console.info(
            `Max: ${max}, min: ${min}, midpoint: ${midPoint}, convertedMidpoint: ${convertedMidpoint} target: ${parsedValue}`,
        );

        // Compare the converted midpoint to the target parsed value
        // The comparison is adjusted based on whether the function is increasing or decreasing

        // If midpoint is greater than the target (accounting for trend direction)
        if ((convertedMidpoint - parsedValue) * overallTrend > 0) {
            max = midPoint; // Search in lower half
        }
        // If midpoint is less than the target (accounting for trend direction)
        else if ((convertedMidpoint - parsedValue) * overallTrend < 0) {
            min = midPoint; // Search in upper half
        }
        // If we found an exact match
        else {
            return midPoint; // Return exact match
        }
    }

    // Return the final approximation (average of min and max)
    return (max + min) / 2;
};
